import {
  BodyText,
  Button,
  Input,
  LinkText,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from '@postscript/components';
import FormikField from 'components/billing/common/FormikField';
import { useGlobalModal } from 'components/GlobalModal/globalModal';
import { useUser } from 'controllers/contexts/user';
import { Form, Formik } from 'formik';
import styled from 'styled-components';
import * as Yup from 'yup';
import { useUpdateContactInfo } from '../../account/AccountView/hooks/useUpdateContactInfo';
import { useResubmitTFNVerification } from '../hooks/useTFNVerification';

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-3);
  margin-top: var(--spacing-3);
`;

const TextSection = styled.div`
  margin-bottom: var(--spacing-3);
`;

const HelperText = styled(BodyText)`
  margin-top: var(--spacing-1);
`;

interface FormValues {
  email: string;
}

const validationSchema = Yup.object({
  email: Yup.string()
    .email('Please enter a valid email address')
    .required('Business email is required'),
});

export default function InvalidBusinessEmailModal(): JSX.Element {
  const { hideModal } = useGlobalModal();
  const {
    user: {
      postscript_contact_email: currentEmail,
      data: { first_name: firstName, last_name: lastName },
      postscript_contact_phone: phone,
    },
  } = useUser();
  const { mutateAsync: updateContactInfo } = useUpdateContactInfo();
  const { mutateAsync: resubmitTFNVerification } = useResubmitTFNVerification();

  const initialValues: FormValues = {
    email: currentEmail ?? '',
  };

  const handleSubmit = async (values: FormValues) => {
    await updateContactInfo({
      firstName: firstName ?? '',
      lastName: lastName ?? '',
      email: values.email,
      phone: phone ?? '',
    });

    await resubmitTFNVerification();
    hideModal();
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      validateOnChange={false}
      validateOnBlur
      enableReinitialize
    >
      {({ isSubmitting }) => (
        <Form>
          <ModalHeader onCancel={hideModal}>
            Toll-Free Number verification denied
          </ModalHeader>
          <ModalBody>
            <TextSection>
              <BodyText bold size="small" color="var(--text-color)">
                Denial reason
              </BodyText>
              <BodyText size="small" color="var(--text-color)">
                Business email must use an official domain (eg.
                name@yourstore.com).
              </BodyText>
            </TextSection>

            <BodyText size="small" color="var(--text-color)">
              <strong>Please update your business email, then resubmit.</strong>{' '}
              Verification typically takes up to 5 business days. Your website
              must remain live during this time.
            </BodyText>

            <FormSection>
              <div>
                <FormikField
                  name="email"
                  label="Business email"
                  placeholder="name@yourstore.com"
                  type="email"
                  as={Input}
                  required
                  fieldSize="medium"
                />
                <HelperText size="x-small" color="var(--gray-5)">
                  You can also find this field under{' '}
                  <LinkText
                    href="/account/settings/general#contact-info"
                    onClick={hideModal}
                  >
                    Shop settings → General → Business contact information
                  </LinkText>
                </HelperText>
              </div>
            </FormSection>
          </ModalBody>
          <ModalFooter>
            <Button type="submit" variant="primary" disabled={isSubmitting}>
              Save and Resubmit
            </Button>
          </ModalFooter>
        </Form>
      )}
    </Formik>
  );
}
